import { BrowserRouter, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Vocabulary } from '@semantical/core/modules/vocabulary';
import type { Workspace } from '@semantical/core/modules/workspace';
import type { WorkspaceConfigRoot } from '@semantical/core/modules/workspace-config';
import { ErrorBoundary } from '@semantical/components/ErrorBoundary';
import { AuthProvider, useAuth } from '@semantical/contexts/AuthContext';
import { AuthGuard } from '@semantical/contexts/AuthGuard';

// Lazy load the main App component for code splitting
const App = lazy(() => import('@semantical/App').then((m) => ({ default: m.App })));

// Public scheme viewer pages (anonymous, outside AuthGuard)
const PublicSchemeViewerPage = lazy(() =>
    import('@semantical/components/public-scheme-viewer/PublicSchemeViewerPage').then((m) => ({
        default: m.PublicSchemeViewerPage,
    })),
);
const PublicSchemeViewerImportPage = lazy(() =>
    import('@semantical/components/public-scheme-viewer/PublicSchemeViewerImportPage').then((m) => ({
        default: m.PublicSchemeViewerImportPage,
    })),
);
const SchemeViewerResolverPage = lazy(() =>
    import('@semantical/components/public-scheme-viewer/SchemeViewerResolverPage').then((m) => ({
        default: m.SchemeViewerResolverPage,
    })),
);
const PublicDiscoverViewerPage = lazy(() =>
    import('@semantical/components/public-scheme-viewer/PublicDiscoverViewerPage').then((m) => ({
        default: m.PublicDiscoverViewerPage,
    })),
);
const HomeGate = lazy(() => import('@semantical/components/landing/HomeGate').then((m) => ({ default: m.HomeGate })));

/**
 * Loading fallback component shown while lazy-loaded components are loading.
 */
function LoadingFallback() {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                backgroundColor: '#1a1a2e',
                color: '#e0e0e0',
                fontFamily: 'system-ui, -apple-system, sans-serif',
            }}
        >
            <div style={{ textAlign: 'center' }}>
                <div
                    style={{
                        width: '40px',
                        height: '40px',
                        border: '3px solid #333',
                        borderTop: '3px solid #4a9eff',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 16px',
                    }}
                />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <p>Loading...</p>
            </div>
        </div>
    );
}

type RouterProps = {
    vocabulary: Vocabulary;
    workspace: Workspace;
    workspaceConfig: WorkspaceConfigRoot;
};

/**
 * Wrapper component that extracts diagramId from URL params and passes to App.
 */
function DiagramRoute({ vocabulary, workspace, workspaceConfig }: RouterProps) {
    const { diagramId } = useParams<{ diagramId: string }>();
    const navigate = useNavigate();
    const auth = useAuth();
    return (
        <App
            vocabulary={vocabulary}
            workspace={workspace}
            workspaceConfig={workspaceConfig}
            diagramId={diagramId!}
            navigateToDiagram={(id) => navigate(`/diagrams/${id}`)}
            navigateToVocabulary={(id) => navigate(`/vocabularies/${encodeURIComponent(id)}`)}
            auth={auth}
        />
    );
}

/**
 * Wrapper component that extracts vocabularyId from URL params and passes to App.
 */
function VocabularyRoute({ vocabulary, workspace, workspaceConfig }: RouterProps) {
    const { vocabularyId, tab } = useParams<{ vocabularyId: string; tab: string }>();
    const navigate = useNavigate();
    const auth = useAuth();
    return (
        <App
            vocabulary={vocabulary}
            workspace={workspace}
            workspaceConfig={workspaceConfig}
            diagramId={undefined}
            vocabularyId={vocabularyId ? decodeURIComponent(vocabularyId) : undefined}
            vocabularyTab={tab}
            navigateToDiagram={(id) => navigate(`/diagrams/${id}`)}
            navigateToVocabulary={(id) => navigate(`/vocabularies/${encodeURIComponent(id)}`)}
            auth={auth}
        />
    );
}

/**
 * Wrapper component that extracts knowledgeGraphId from URL params and passes to App.
 */
function KnowledgeGraphRoute({ vocabulary, workspace, workspaceConfig }: RouterProps) {
    const { knowledgeGraphId } = useParams<{ knowledgeGraphId: string }>();
    const navigate = useNavigate();
    const auth = useAuth();
    return (
        <App
            vocabulary={vocabulary}
            workspace={workspace}
            workspaceConfig={workspaceConfig}
            diagramId={undefined}
            knowledgeGraphId={knowledgeGraphId ? decodeURIComponent(knowledgeGraphId) : undefined}
            navigateToDiagram={(id) => navigate(`/diagrams/${id}`)}
            navigateToVocabulary={(id) => navigate(`/vocabularies/${encodeURIComponent(id)}`)}
            auth={auth}
        />
    );
}

/**
 * Wrapper component that extracts ontologyDatasetId from URL params and passes to App.
 */
function OntologyRoute({ vocabulary, workspace, workspaceConfig }: RouterProps) {
    const { ontologyDatasetId, tab } = useParams<{ ontologyDatasetId: string; tab: string }>();
    const navigate = useNavigate();
    const auth = useAuth();
    return (
        <App
            vocabulary={vocabulary}
            workspace={workspace}
            workspaceConfig={workspaceConfig}
            diagramId={undefined}
            ontologyDatasetId={ontologyDatasetId ? decodeURIComponent(ontologyDatasetId) : undefined}
            ontologyTab={tab}
            navigateToDiagram={(id) => navigate(`/diagrams/${id}`)}
            navigateToVocabulary={(id) => navigate(`/vocabularies/${encodeURIComponent(id)}`)}
            auth={auth}
        />
    );
}

/**
 * Main router component.
 *
 * Routes:
 * - /                    → App with no active diagram (editor closed)
 * - /diagrams/:id        → Load/create diagram by ID
 * - /vocabularies/:id    → View vocabulary by ID (URI encoded)
 * - /knowledge-graphs/:id → View knowledge graph by dataset ID
 * - /ontologies/:id      → View ontology by dataset ID
 *
 * Wrapped with ErrorBoundary for crash recovery and Suspense for lazy loading.
 */
export function AppRouter({ vocabulary, workspace, workspaceConfig }: RouterProps) {
    return (
        <AuthProvider>
            <ErrorBoundary>
                <BrowserRouter>
                    <Suspense fallback={<LoadingFallback />}>
                        <Routes>
                            {/* Public, anonymous scheme viewer routes — outside AuthGuard. */}
                            <Route path="/schemeViewer" element={<SchemeViewerResolverPage />} />
                            <Route path="/schemeViewer/import" element={<PublicSchemeViewerImportPage />} />
                            <Route
                                path="/vocabularies/:vocabularyId/schemeViewer"
                                element={<PublicSchemeViewerPage />}
                            />
                            <Route path="/vocabularies/:vocabularyId/discover" element={<PublicDiscoverViewerPage />} />
                            {/* Everything else is gated by AuthGuard. */}
                            <Route
                                path="/*"
                                element={
                                    <AuthGuard>
                                        <AuthedRoutes
                                            vocabulary={vocabulary}
                                            workspace={workspace}
                                            workspaceConfig={workspaceConfig}
                                        />
                                    </AuthGuard>
                                }
                            />
                        </Routes>
                    </Suspense>
                </BrowserRouter>
            </ErrorBoundary>
        </AuthProvider>
    );
}

function AuthedRoutes({ vocabulary, workspace, workspaceConfig }: RouterProps) {
    return (
        <Routes>
            <Route
                path="/"
                element={<HomeGate vocabulary={vocabulary} workspace={workspace} workspaceConfig={workspaceConfig} />}
            />
            <Route
                path="/diagrams/:diagramId"
                element={
                    <DiagramRoute vocabulary={vocabulary} workspace={workspace} workspaceConfig={workspaceConfig} />
                }
            />
            <Route
                path="/vocabularies/:vocabularyId/:tab"
                element={
                    <VocabularyRoute vocabulary={vocabulary} workspace={workspace} workspaceConfig={workspaceConfig} />
                }
            />
            <Route
                path="/vocabularies/:vocabularyId"
                element={
                    <VocabularyRoute vocabulary={vocabulary} workspace={workspace} workspaceConfig={workspaceConfig} />
                }
            />
            <Route
                path="/knowledge-graphs/:knowledgeGraphId"
                element={
                    <KnowledgeGraphRoute
                        vocabulary={vocabulary}
                        workspace={workspace}
                        workspaceConfig={workspaceConfig}
                    />
                }
            />
            <Route
                path="/ontologies/:ontologyDatasetId/:tab"
                element={
                    <OntologyRoute vocabulary={vocabulary} workspace={workspace} workspaceConfig={workspaceConfig} />
                }
            />
            <Route
                path="/ontologies/:ontologyDatasetId"
                element={
                    <OntologyRoute vocabulary={vocabulary} workspace={workspace} workspaceConfig={workspaceConfig} />
                }
            />
        </Routes>
    );
}
